import React from "react"
import styled from "@emotion/styled"

import Layout from "../components/layout"
import SEO from "../components/seo"

const Intro = styled.p`
  font-size: 1.2rem;
  line-height: 1.7;
  margin-bottom: 2.5rem;
  margin-top: 0;
`

const Hook = styled.p`
  font-size: 1.5rem;
  font-weight: 700;
  line-height: 1.3;
  margin: 1rem 0 1.5rem;
`

const PullQuote = styled.p`
  font-size: 1.15rem;
  font-style: italic;
  font-weight: 300;
  border-left: 3px solid rgba(0, 0, 0, 0.15);
  padding-left: 1.2rem;
  margin: 2rem 0;
  color: rgba(0, 0, 0, 0.65);
`

const PrincipleLabel = styled.span`
  font-weight: 700;
  display: block;
  margin-bottom: 0.2rem;
`

const Principle = styled.div`
  margin-bottom: 1.2rem;
`

const Closing = styled.div`
  font-size: 1.1rem;
  line-height: 1.7;

  p:last-child {
    font-weight: 700;
    font-style: italic;
  }
`

const Disclaimer = styled.div`
  margin-top: 3rem;
  margin-bottom: 2.5rem;
  padding-top: 1.5rem;
  border-top: 1px solid rgba(0, 0, 0, 0.1);
  color: rgba(0, 0, 0, 0.45);
  font-size: 0.85rem;
  font-style: italic;
`

const BantaniumPage = () => (
  <Layout>
    <SEO
      title="Bantanium"
      description="A personal research platform exploring whether AI can build a longitudinal understanding of individual metabolic health."
    />

    <h1>Bantanium</h1>

    <Intro>I started Bantanium because I wanted to answer a simple question:</Intro>

    <Hook>How does my body actually work?</Hook>

    <p>
      Living with PCOS/PMOS made me realize how difficult that question is to
      answer. Despite unprecedented access to health data, most advice is
      still remarkably generic. Two people can eat the same meal, follow the
      same training plan, or take the same medication and experience
      completely different outcomes. Yet our tools rarely help us understand
      why.
    </p>

    <p>
      What began as a personal question has grown into a broader one:{" "}
      <strong>
        What would it take to build a system that truly understands an
        individual over time?
      </strong>
    </p>

    <hr />

    <h2>Why the PMOS Renaming Matters</h2>

    <p>
      The transition from <strong>Polycystic Ovary Syndrome (PCOS)</strong>{" "}
      to <strong>Polyendocrine Metabolic Ovarian Syndrome (PMOS)</strong> is
      more than a name change — it's an acknowledgment that we've
      misunderstood the disease for decades. The old name emphasized ovarian
      cysts, even though many patients don't have them, while overlooking the
      metabolic and endocrine dysfunction at the core of the condition.
    </p>

    <p>
      To me, the significance isn't the terminology itself. It's that an
      entire field has agreed the original framing no longer describes the
      problem. Medicine is beginning to recognize PMOS as a complex, lifelong
      metabolic condition, but most consumer health products still reflect
      the old mental model: disconnected symptom trackers, calorie counters,
      and generic advice.
    </p>

    <p>
      That gap is where I see opportunity. The science has evolved faster
      than the software. If our understanding of the disease has
      fundamentally changed, then perhaps the tools should change too — not
      by offering more information, but by helping each person build a
      personalized understanding of their own physiology over time.
    </p>

    <hr />

    <p>
      Today's health ecosystem is fragmented. Wearables measure movement and
      sleep. Nutrition apps track food. Labs capture biomarkers. Clinicians
      see snapshots during appointments. Each contributes a piece of the
      story, but none maintains an evolving understanding of the person
      behind the data.
    </p>

    <p>I don't believe we're missing data.</p>

    <p>I believe we're missing understanding.</p>

    <p>
      No clinician — no matter how skilled or attentive — has the time or
      memory to continuously synthesize years of sleep, nutrition, symptoms,
      biomarkers, and movement into an evolving model of a single patient. I
      believe AI may finally make that possible.
    </p>

    <p>
      Healthcare doesn't need another chatbot that summarizes yesterday's
      metrics. It needs systems that learn alongside us — forming
      hypotheses, recognizing patterns, updating beliefs as new evidence
      arrives, and making uncertainty explicit rather than hiding it.
    </p>

    <p>That's what Bantanium exists to explore.</p>

    <hr />

    <h2>Questions I'm Exploring</h2>

    <p>Rather than beginning with a product, I'm beginning with questions.</p>

    <ul>
      <li>What signals genuinely matter over months and years?</li>
      <li>Which relationships are causal, and which are coincidence?</li>
      <li>
        How should a system update its understanding when new evidence
        contradicts previous assumptions?
      </li>
      <li>
        How can uncertainty become something a system communicates instead
        of conceals?
      </li>
      <li>
        What does personalized health look like when it learns continuously
        instead of starting over at every appointment?
      </li>
    </ul>

    <p>These questions matter more to me than any particular implementation.</p>

    <hr />

    <h2>Current Experiments</h2>

    <p>Bantanium is my research environment for exploring those questions.</p>

    <p>
      I'm collecting and integrating longitudinal data from wearables,
      nutrition logs, sleep records, symptoms, biomarkers, and continuous
      glucose monitoring. Alongside that, I'm studying existing health
      products, reading scientific literature, and building small prototypes
      to understand where current approaches succeed — and where they fall
      short.
    </p>

    <PullQuote>I want to earn the right to build.</PullQuote>

    <p>
      The goal isn't to prove a preconceived idea. It's to become
      increasingly difficult to fool myself.
    </p>

    <hr />

    <h2>Principles</h2>

    <Principle>
      <PrincipleLabel>Understand before prescribing.</PrincipleLabel>
      The system should help uncover patterns before turning them into
      recommendations.
    </Principle>

    <Principle>
      <PrincipleLabel>Longitudinal over episodic.</PrincipleLabel>
      Health is a continuous process, not a series of isolated appointments.
    </Principle>

    <Principle>
      <PrincipleLabel>Hypotheses over declarations.</PrincipleLabel>
      Every insight should remain open to revision as new evidence appears.
    </Principle>

    <Principle>
      <PrincipleLabel>Personal without being isolated.</PrincipleLabel>
      Individual models should remain grounded in scientific evidence and
      informed by clinical expertise.
    </Principle>

    <Principle>
      <PrincipleLabel>User-owned by default.</PrincipleLabel>
      People should retain control over their own health data, with
      transparent methods and explicit consent.
    </Principle>

    <hr />

    <h2>Looking Ahead</h2>

    <Closing>
      <p>I don't know what Bantanium will ultimately become.</p>

      <p>
        It may become an AI research platform, a clinical decision-support
        tool, a consumer product, or simply the foundation for a better
        question than the ones I'm asking today.
      </p>

      <p>
        I don't know the answers yet. I'm still trying to understand which
        signals matter, which relationships are meaningful, how AI should
        reason about uncertainty, and what a truly personalized health system
        should feel like to the people who need it most.
      </p>

      <p>I expect many of my assumptions to be wrong. That's part of the process.</p>

      <p>The goal isn't to defend an idea.</p>

      <p>It's to discover one that's true.</p>
    </Closing>

    <Disclaimer>
      Bantanium is a personal research project, not a medical device. Health
      decisions should be made with qualified healthcare professionals. For
      research conversations or thoughtful critiques:{" "}
      <a href="mailto:ndpbanta@gmail.com">ndpbanta@gmail.com</a>
    </Disclaimer>
  </Layout>
)

export default BantaniumPage
